import * as Api from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { IPeopleSearchAddedTagsState, PersistentStoragePeopleSearchAddedTagsStateKey } from '../../../../../models';
import { useEventLogging } from '../../../../../models/Logging';
import { PersistentStorageManager } from '../../../../../models/Storage';
import { useContactSuggestedTagsQuery } from '../../../../../queries';
import { AutoCompleteSearchField } from '../../../../components/autocomplete/AutoCompleteSearchField';
import { ContactsGutterTag } from '../../../../components/contacts/ContactsGutterTag';
import { useTagsStorage } from '../../../../components/entities/tags/TagsEditor/hooks';
import { TagsList } from '../../../../components/entities/tags/TagsEditor/presentation';
import { HistoryIcon } from '../../../../components/svgs/icons/HistoryIcon';
import { SearchIcon } from '../../../../components/svgs/icons/SearchIcon';
import { green4, navigation } from '../../../../styles/colors';
import { PeopleCollapsibleSection } from '../PeopleCollapsibleSection';
import { compoundFiltersStyles } from './styles';

interface IProps {
	className?: string;
	styles?: StyleDeclarationValue[];
	filter: Api.IBulkContactsRequest;
	onTagAdded(tag: string): void;
	onTagRemoved(tag: string): void;
	tags: string[];
}

interface IGutterTag {
	isActive: boolean;
	onRemoveButtonClicked(): void;
	showRemoveButton: boolean;
	tag: string;
	updateSelectedTagList(): void;
}

export const PopularTagsV2: React.FC<IProps> = observer(({ className, styles, onTagAdded, onTagRemoved, tags }) => {
	const { logEvent } = useEventLogging();
	const [userAddedTags, setUserAddedTags] = React.useState<string[]>([]);
	const [isOpen, setIsOpen] = React.useState(false);
	const { recentSearches, updateTagsRecentSearches } = useTagsStorage();
	const [inputValue, setInputValue] = React.useState('');

	const tagsQuery = useContactSuggestedTagsQuery({
		addKeepInTouchTag: false,
		pageSize: 25,
	});
	const suggestedTags = React.useMemo(() => {
		return (tagsQuery.data as Api.IAccountTag[]) || [];
	}, [tagsQuery.data]);

	React.useEffect(() => {
		loadStoredTags();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	React.useEffect(() => {
		updateStoredTags();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [userAddedTags]);

	const isTagSelected = (tagToCheck: string) => {
		return tags.includes(tagToCheck);
	};

	const userAdded = () => {
		return Array.from(new Set([...userAddedTags, ...tags].filter(x => !suggestedTags.map(y => y.name).includes(x))));
	};

	const toggleTagOnSelectedTagList = (tag: string) => () => {
		updateTagsRecentSearches(tag);

		const alreadySelected = isTagSelected(tag);

		if (alreadySelected) {
			// remove tag from the list
			onTagRemoved(tag);
		} else {
			// add tag to the list, or create an empty list if not present
			onTagAdded(tag);
		}

		setInputValue('');
	};

	const onAutocompleteTagSelected = (tag: string) => {
		if ((suggestedTags.includes(tag) || userAddedTags.includes(tag)) && !tags.includes(tag)) {
			toggleTagOnSelectedTagList(tag)();
		} else if (!userAddedTags.includes(tag) && !suggestedTags.includes(tag)) {
			setUserAddedTags([...userAddedTags, tag]);
			toggleTagOnSelectedTagList(tag)();
		}
		logEvent('AddTagToGutter', { tag: tag.length });
	};

	const removeTagFromUserAddedTagList = (tag: string) => () => {
		setUserAddedTags(userAddedTags.filter(x => x !== tag));

		if (tags.includes(tag)) {
			toggleTagOnSelectedTagList(tag)();
		}
	};

	const updateStoredTags = () => {
		PersistentStorageManager.local.setObject<IPeopleSearchAddedTagsState>(
			PersistentStoragePeopleSearchAddedTagsStateKey,
			{
				tags: Array.from(new Set([...userAddedTags])),
			}
		);
	};

	const loadStoredTags = () => {
		PersistentStorageManager.local
			.getObject<IPeopleSearchAddedTagsState>(PersistentStoragePeopleSearchAddedTagsStateKey)
			.then(storedPeopleTagsState => {
				const userStoredTags = storedPeopleTagsState?.tags ?? [];
				if (userStoredTags.length === 0) {
					PersistentStorageManager.local.remove(PersistentStoragePeopleSearchAddedTagsStateKey);
				}
				const userAddedTagsLocal = Array.from(new Set([...userAddedTags, ...userStoredTags]));
				setUserAddedTags(userAddedTagsLocal);
			});
	};
	const createGutterTag = (tag: string, isUserAdded: boolean) => {
		const isActive: boolean = isTagSelected(tag);
		return {
			isActive,
			onRemoveButtonClicked: removeTagFromUserAddedTagList(tag),
			showRemoveButton: isUserAdded,
			tag,
			updateSelectedTagList: toggleTagOnSelectedTagList(tag),
		};
	};

	const renderTags: IGutterTag[] = [
		...(suggestedTags?.map((tag: Api.IAccountTag) => {
			return createGutterTag(tag.name, false);
		}) || []),
		...(userAdded().map(tag => createGutterTag(tag, true)) || []),
	];
	const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setInputValue(e.target.value);
	};

	const emptyResults =
		recentSearches?.length > 0 ? (
			<>
				{!inputValue ? (
					<TagsList
						title='Recent Searches'
						icon={<HistoryIcon fillColor={green4} className={css(compoundFiltersStyles.sectionIcon)} />}
						altKey='searches'
						list={recentSearches.slice(0, 6)}
						addTag={tag => onAutocompleteTagSelected(tag?.name)}
					/>
				) : null}
				{inputValue ? (
					<p className={css(compoundFiltersStyles.noMargin)} key={`${inputValue}-pending`} title={inputValue}>
						<span className={css(compoundFiltersStyles.createTag)}>{`No Results found for "${inputValue}"`}</span>
					</p>
				) : null}
			</>
		) : undefined;

	return (
		<div className={`${css(compoundFiltersStyles.container, ...(styles || []))} ${className || ''}`}>
			<PeopleCollapsibleSection
				showLoader={tagsQuery.isPending}
				items={renderTags.map(tag => {
					return (
						<ContactsGutterTag
							className={css(compoundFiltersStyles.tag)}
							key={tag.tag}
							isActive={tag.isActive}
							onRemoveButtonClicked={removeTagFromUserAddedTagList(tag.tag)}
							showRemoveButton={tag.showRemoveButton}
							tag={tag.tag}
							updateSelectedTagList={toggleTagOnSelectedTagList(tag.tag)}
						/>
					);
				})}
				itemHeightPx={30}
			/>
			<div>
				<AutoCompleteSearchField
					sectionTitleElement={
						<>
							<div className={css(compoundFiltersStyles.sectionWrap)}>
								<p className={css(compoundFiltersStyles.sectionTitle)}>Top Results</p>
							</div>
						</>
					}
					emptyResults={emptyResults}
					anchorClassName={css(compoundFiltersStyles.searchBox)}
					clearSearchFieldAfterSelectingItem={true}
					dropdownContentClassName={css(compoundFiltersStyles.autocompleteBox)}
					inputId='contactsGutterTagSearchInput'
					inputProps={{
						placeholder: 'Search tags',
						onChange: handleValueChange,
						onFocus: () => setIsOpen(true),
						onBlur: () => setIsOpen(false),
					}}
					leftAccessory={
						<span className={css(compoundFiltersStyles.searchIcon)}>
							<SearchIcon fillColor={navigation} />
						</span>
					}
					onItemSelected={onAutocompleteTagSelected}
					resultsLimit={25}
					pageSize={25}
					type={Api.ResourceAutoCompleteViewModelType.Tag}
					openOnFocus={Boolean(emptyResults)}
					isOpen={Boolean(emptyResults) || isOpen}
				/>
			</div>
		</div>
	);
});
